/* ==========================================================================
   Listicle — page behaviour, shared by every lst-* section.

   Every section links this file, so the script runs once per section on the
   page. The guard below makes all but the first run a no-op; everything else
   here is written to work no matter which sections are present, absent or
   reordered in the theme editor.

   1. Viewport-aware autoplaying videos
   2. Social-proof and buy-box counters
   3. Sale countdown
   4. Sticky CTA
   5. Suppression of app UI injected into the page
   ========================================================================== */

(function () {
  'use strict';

  if (window.__lstListicleInit) return;
  window.__lstListicleInit = true;

  var ready = function (fn) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', fn);
    } else {
      fn();
    }
  };

  ready(function () {
    /* -------------------------------------------------------------------- 1
       Only the clips on screen actually run — seven simultaneous loops would
       cost battery on a phone for no visible benefit. */
    var videos = Array.prototype.slice.call(document.querySelectorAll('.lst video'));

    function play(video) {
      video.muted = true;
      var attempt = video.play();
      if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
    }

    if (videos.length) {
      if ('IntersectionObserver' in window) {
        var vObserver = new IntersectionObserver(function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) play(entry.target);
            else if (!entry.target.paused) entry.target.pause();
          });
        }, { rootMargin: '200px 0px', threshold: 0.01 });
        videos.forEach(function (v) { vObserver.observe(v); });
      } else {
        videos.forEach(play);
      }
    }

    /* -------------------------------------------------------------------- 2
       Counters. Climb from data-count-from to data-count-to on an ease-out
       curve, hold, then run again. The numbers live in the markup, so the
       theme editor drives them. Reduced-motion just shows the final figure. */
    var still = window.matchMedia('(prefers-reduced-motion: reduce)');

    Array.prototype.forEach.call(
      document.querySelectorAll('.lst [data-count-to]'),
      function (counter) {
        var from = parseInt(counter.getAttribute('data-count-from'), 10) || 0;
        var to = parseInt(counter.getAttribute('data-count-to'), 10) || 0;
        var locale = counter.getAttribute('data-count-locale') || 'en-IN';
        var COUNT_MS = 2000;
        var HOLD_MS = 10000;

        var render = function (n) {
          counter.textContent = Math.floor(n).toLocaleString(locale);
        };

        var run = function () {
          if (still.matches) { render(to); return; }
          var start = 0;
          var step = function (now) {
            if (!start) start = now;
            var t = Math.min((now - start) / COUNT_MS, 1);
            // easeOutExpo — fast off the mark, then a long settle.
            render(from + (t === 1 ? 1 : 1 - Math.pow(2, -10 * t)) * (to - from));
            if (t < 1) requestAnimationFrame(step);
            else { render(to); setTimeout(run, HOLD_MS); }
          };
          render(from);
          requestAnimationFrame(step);
        };

        if ('IntersectionObserver' in window) {
          var watch = new IntersectionObserver(function (entries) {
            if (!entries[0].isIntersecting) return;
            watch.disconnect();
            run();
          }, { threshold: 0.5 });
          watch.observe(counter);
        } else {
          run();
        }
      }
    );

    /* -------------------------------------------------------------------- 3
       Countdown. The deadline is stored per visitor so the clock doesn't
       reset on every page view; when it runs out a fresh window starts. */
    Array.prototype.forEach.call(
      document.querySelectorAll('.lst [data-countdown-hours]'),
      function (el) {
        var hours = parseFloat(el.getAttribute('data-countdown-hours')) || 2;
        var key = 'lst_timer_' + (el.getAttribute('data-countdown-key') || 'default');
        var duration = hours * 60 * 60 * 1000;
        var end;

        try {
          var stored = localStorage.getItem(key);
          end = stored ? parseInt(stored, 10) : 0;
          if (!end || Date.now() > end) {
            end = Date.now() + duration;
            localStorage.setItem(key, end);
          }
        } catch (e) {
          // Private browsing, or storage is blocked. Fall back to this visit.
          end = Date.now() + duration;
        }

        var pad = function (n) { return String(n).padStart(2, '0'); };
        var tick = function () {
          var diff = Math.max(0, end - Date.now());
          el.textContent =
            pad(Math.floor(diff / 3600000)) + ':' +
            pad(Math.floor((diff % 3600000) / 60000)) + ':' +
            pad(Math.floor((diff % 60000) / 1000));
        };
        tick();
        setInterval(tick, 1000);
      }
    );

    /* -------------------------------------------------------------------- 4
       Sticky CTA appears once its anchor section has scrolled out of view. */
    var sticky = document.querySelector('.lst [data-sticky-cta]');
    var firstReason = document.querySelector('.lst [data-reason]');
    var anchor = firstReason || document.querySelector('.lst [data-hero]');

    if (sticky && anchor) {
      var isVisible = false;
      var sync = function () {
        var past = anchor.getBoundingClientRect().bottom < 0;
        if (past === isVisible) return;
        isVisible = past;
        sticky.classList.toggle('is-visible', past);
      };
      window.addEventListener('scroll', sync, { passive: true });
      window.addEventListener('resize', sync);
      sync();
    }

    /* -------------------------------------------------------------------- 5
       Shopify renders app embed blocks itself, outside the theme's control,
       so `layout none` does not keep them off this page. This store's
       MoonBundle widget was mounting under the buy box. Strip its nodes and
       keep stripping, because apps mount asynchronously and can re-mount.

       Deliberately narrow: only nodes this store's bundle app names. Nothing
       else on the page is touched. Add to APP_UI if another app intrudes. */
    var APP_UI = [
      '[id*="moonbundle" i]', '[class*="moonbundle" i]',
      '[id*="moon-bundle" i]', '[class*="moon-bundle" i]',
      '[id*="moon_bundle" i]', '[class*="moon_bundle" i]',
      'moon-bundles', 'moon-bundle'
    ].join(', ');

    var strip = function () {
      Array.prototype.forEach.call(
        document.querySelectorAll(APP_UI),
        function (el) { el.remove(); }
      );
    };

    strip();
    new MutationObserver(strip).observe(document.body, { childList: true, subtree: true });
  });
})();
